import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  BarChart3, Users, Clock, AlertTriangle, Calendar, TrendingUp,
  Home, RefreshCw, CheckCircle2, XCircle, Timer, UserCheck, Zap
} from 'lucide-react';

function MetricCard({ title, value, subtitle, icon: Icon, trend, color = 'default' }) {
  const colorClasses = {
    default: 'text-foreground',
    green: 'text-green-600',
    red: 'text-red-600',
    amber: 'text-amber-600',
    blue: 'text-blue-600',
    purple: 'text-purple-600',
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className={`text-3xl font-bold ${colorClasses[color]}`}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
            {trend && (
              <div className="flex items-center gap-1 text-xs">
                <TrendingUp className={`w-3 h-3 ${trend > 0 ? 'text-green-500' : 'text-red-500'}`} />
                <span className={trend > 0 ? 'text-green-600' : 'text-red-600'}>
                  {trend > 0 ? '+' : ''}{trend}% vs last week
                </span>
              </div>
            )}
          </div>
          <div className={`p-2 rounded-lg bg-muted/50 ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StaffUtilizationBar({ name, hours, maxHours = 50 }) {
  const percentage = Math.min((hours / maxHours) * 100, 100);
  const isOvertime = hours > 45;
  const isWarning = hours > 38 && hours <= 45;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="truncate max-w-[200px]">{name}</span>
        <span className={`font-mono ${isOvertime ? 'text-red-600 font-semibold' : isWarning ? 'text-amber-600' : ''}`}>
          {hours.toFixed(1)}h
        </span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            isOvertime ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-blue-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

export default function WorkforceDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedDate ? `?date=${selectedDate}` : '';
      const res = await api.get(`/roster/dashboard/workforce${params}`);
      setData(res.data.data);
    } catch (e) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const metrics = data?.metrics || {};
  const weekRange = data?.weekRange || {};
  const staffWithHighHours = data?.staffWithHighHours || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="workforce-dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-500" />
            Workforce Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Real-time workforce metrics and scheduling insights
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-[160px]"
            data-testid="dashboard-date-picker"
          />
          <Button variant="outline" onClick={fetchData} data-testid="dashboard-refresh">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {data && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="w-4 h-4" />
          <span>
            Reference: {data.date} | Week: {weekRange.start} to {weekRange.end}
          </span>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading dashboard...</div>
      ) : !data ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No data available</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Primary Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              title="Shifts Today"
              value={metrics.shiftsToday || 0}
              icon={Calendar}
              color="blue"
            />
            <MetricCard
              title="Unfilled Shifts"
              value={metrics.unfilledShifts || 0}
              subtitle="Needs attention"
              icon={AlertTriangle}
              color={metrics.unfilledShifts > 0 ? 'red' : 'green'}
            />
            <MetricCard
              title="Open Shifts (Week)"
              value={metrics.openShifts || 0}
              subtitle="Available for pickup"
              icon={Clock}
              color={metrics.openShifts > 3 ? 'amber' : 'default'}
            />
            <MetricCard
              title="Recurring Series"
              value={metrics.recurringShifts || 0}
              subtitle="Active patterns"
              icon={RefreshCw}
              color="purple"
            />
          </div>

          {/* Secondary Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              title="Staff Utilization"
              value={`${metrics.staffUtilization || 0}%`}
              subtitle="Average this week"
              icon={UserCheck}
              color={metrics.staffUtilization > 80 ? 'red' : metrics.staffUtilization > 60 ? 'green' : 'amber'}
            />
            <MetricCard
              title="Compliance Risks"
              value={metrics.complianceRisks || 0}
              subtitle="Staff non-compliant"
              icon={AlertTriangle}
              color={metrics.complianceRisks > 0 ? 'red' : 'green'}
            />
            <MetricCard
              title="Overtime Risk"
              value={metrics.overtimeRisk || 0}
              subtitle="Staff over 45h"
              icon={Timer}
              color={metrics.overtimeRisk > 0 ? 'amber' : 'green'}
            />
            <MetricCard
              title="SIL Coverage"
              value={`${metrics.silCoverage || 0}%`}
              subtitle="Houses with shifts today"
              icon={Home}
              color={metrics.silCoverage >= 80 ? 'green' : metrics.silCoverage >= 50 ? 'amber' : 'red'}
            />
          </div>

          {/* Staff Hours */}
          {staffWithHighHours.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-500" />
                  High Hours This Week
                </CardTitle>
                <CardDescription>
                  Staff members with more than 40 hours scheduled
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {staffWithHighHours.map((s, i) => (
                    <StaffUtilizationBar
                      key={s.staff_id || i}
                      name={s.staff_name || `Staff ${i + 1}`}
                      hours={s.hours}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" onClick={() => window.location.href = '/admin/roster'}>
                  <Calendar className="w-4 h-4 mr-2" />
                  View All Shifts
                </Button>
                <Button variant="outline" onClick={() => window.location.href = '/roster/recurring'}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Manage Recurring
                </Button>
                <Button variant="outline" onClick={() => window.location.href = '/roster/templates'}>
                  <Clock className="w-4 h-4 mr-2" />
                  Shift Templates
                </Button>
                <Button variant="outline" onClick={() => window.location.href = '/roster/swap-requests'}>
                  <Users className="w-4 h-4 mr-2" />
                  Swap Requests
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Status Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className={metrics.unfilledShifts === 0 ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'}>
              <CardContent className="p-4 flex items-center gap-3">
                {metrics.unfilledShifts === 0 ? (
                  <>
                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                    <div>
                      <p className="font-semibold text-green-700">All Shifts Covered</p>
                      <p className="text-sm text-green-600">Today's roster is fully staffed</p>
                    </div>
                  </>
                ) : (
                  <>
                    <XCircle className="w-8 h-8 text-red-500" />
                    <div>
                      <p className="font-semibold text-red-700">{metrics.unfilledShifts} Shifts Need Staff</p>
                      <p className="text-sm text-red-600">Review and assign or broadcast</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className={metrics.complianceRisks === 0 ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50/50'}>
              <CardContent className="p-4 flex items-center gap-3">
                {metrics.complianceRisks === 0 ? (
                  <>
                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                    <div>
                      <p className="font-semibold text-green-700">All Staff Compliant</p>
                      <p className="text-sm text-green-600">No compliance issues detected</p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-8 h-8 text-amber-500" />
                    <div>
                      <p className="font-semibold text-amber-700">{metrics.complianceRisks} Compliance Alerts</p>
                      <p className="text-sm text-amber-600">Review staff compliance status</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className={metrics.overtimeRisk === 0 ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50/50'}>
              <CardContent className="p-4 flex items-center gap-3">
                {metrics.overtimeRisk === 0 ? (
                  <>
                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                    <div>
                      <p className="font-semibold text-green-700">No Overtime Risk</p>
                      <p className="text-sm text-green-600">All staff within limits</p>
                    </div>
                  </>
                ) : (
                  <>
                    <Timer className="w-8 h-8 text-amber-500" />
                    <div>
                      <p className="font-semibold text-amber-700">{metrics.overtimeRisk} Staff Overtime</p>
                      <p className="text-sm text-amber-600">Consider redistributing shifts</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
